import { useEffect, useState } from 'react';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { REMEMBERED_CATEGORY_KEY } from '@/lib/consent';
import { safeLocalStorage, safeSessionStorage } from '@/lib/storage';

/**
 * The product category a visitor last picked.
 *
 * - Kept for the browser session (sessionStorage), so going to a product and pressing
 *   back keeps the filter. Cleared when the tab closes.
 * - Remembered across visits (localStorage) only while the visitor has accepted
 *   optional storage; rejecting or withdrawing consent removes it.
 * - With `enabled = false` (e.g. the Home page preview) storage is ignored entirely and
 *   `defaultValue` is always used, so a filter chosen elsewhere can't hide products here.
 */
export function useRememberedCategory(enabled: boolean, defaultValue: string) {
  const { hasConsented } = useCookieConsent();

  const [category, setCategory] = useState<string>(() => {
    if (!enabled) return defaultValue;
    return (
      safeSessionStorage.getItem(REMEMBERED_CATEGORY_KEY) ??
      (hasConsented ? safeLocalStorage.getItem(REMEMBERED_CATEGORY_KEY) : null) ??
      defaultValue
    );
  });

  useEffect(() => {
    if (!enabled) return;

    safeSessionStorage.setItem(REMEMBERED_CATEGORY_KEY, category);
    if (hasConsented) {
      safeLocalStorage.setItem(REMEMBERED_CATEGORY_KEY, category);
    } else {
      safeLocalStorage.removeItem(REMEMBERED_CATEGORY_KEY);
    }
  }, [enabled, category, hasConsented]);

  return [category, setCategory] as const;
}
